import React, { useEffect, useMemo, useState } from "react";
import { ArrowDownRight, ArrowUpRight, BarChart3, FileSpreadsheet, LineChart as LineChartIcon, TrendingUp } from "lucide-react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { downloadFinanceExport, euro, practiceApi } from "../../lib/practiceApi";
import { MonthlyReportButton } from "./ReceivablesPanel";
import "./finance.css";

const compact = value => new Intl.NumberFormat("it-IT", { notation: "compact", maximumFractionDigits: 1 }).format(value || 0);

export const FinanceChart = () => {
  const [data, setData] = useState(null);
  const [year, setYear] = useState("");
  const [mode, setMode] = useState("bar");
  const [error, setError] = useState("");

  useEffect(() => {
    practiceApi(`/finance/analytics${year ? `?year=${year}` : ""}`).then(setData).catch(err => setError(err.message));
  }, [year]);

  const series = useMemo(() => {
    if (!data) return [];
    return year === "all" ? data.yearly : data.monthly;
  }, [data, year]);

  if (error) return <div className="error" role="alert" data-testid="finance-chart-error">{error}</div>;
  if (!data) return <div className="finance-chart-card" data-testid="finance-chart-loading"><p className="muted">Caricamento andamento economico…</p></div>;

  const totals = year === "all" ? data.overall : data.totals;
  const showPrevious = year !== "all" && data.has_previous;
  const Chart = mode === "bar" ? BarChart : AreaChart;

  return <section className="finance-chart-card" data-testid="finance-chart">
    <header className="finance-chart-head">
      <div>
        <span className="finance-kicker"><TrendingUp size={13} />ANDAMENTO ECONOMICO</span>
        <h2>Compensi e incassi {year === "all" ? "per anno" : `· ${data.selected}`}</h2>
      </div>
      <div className="finance-controls">
        <select data-testid="finance-year-select" value={year || data.selected} onChange={e => setYear(e.target.value)}>
          {data.years.map(value => <option key={value} value={value}>{value}</option>)}
          <option value="all">Tutti gli anni</option>
        </select>
        <MonthlyReportButton />
        <button className="finance-export" data-testid="finance-export" onClick={() => downloadFinanceExport(year || data.selected).catch(err => setError(err.message))}><FileSpreadsheet size={15} />Esporta Excel</button>
        <div className="finance-toggle">
          <button className={mode === "bar" ? "active" : ""} data-testid="finance-mode-bar" onClick={() => setMode("bar")} title="Istogramma"><BarChart3 size={15} /></button>
          <button className={mode === "area" ? "active" : ""} data-testid="finance-mode-area" onClick={() => setMode("area")} title="Andamento"><LineChartIcon size={15} /></button>
        </div>
      </div>
    </header>

    <div className="finance-kpis">
      <div><span>Totale compensi</span><strong data-testid="finance-kpi-fees">{euro(totals.invoiced)}</strong>{showPrevious && <Delta value={data.growth.invoiced} testId="finance-growth-fees" />}</div>
      <div><span>Incassato (acconti e pagamenti)</span><strong data-testid="finance-kpi-collected">{euro(totals.collected)}</strong>{showPrevious && <Delta value={data.growth.collected} testId="finance-growth-collected" />}</div>
      <div><span>Da incassare</span><strong data-testid="finance-kpi-outstanding">{euro(totals.outstanding)}</strong></div>
      <div><span>Percentuale incassata</span><strong data-testid="finance-kpi-rate">{String(totals.collection_rate).replace(".", ",")}%</strong></div>
    </div>

    <div className="finance-chart-body">
      <ResponsiveContainer width="100%" height={280}>
        <Chart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="invoicedFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#2563EB" stopOpacity={0.55} />
              <stop offset="100%" stopColor="#2563EB" stopOpacity={0.04} />
            </linearGradient>
            <linearGradient id="collectedFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0F2747" stopOpacity={0.6} />
              <stop offset="100%" stopColor="#0F2747" stopOpacity={0.04} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#EDE9E3" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#8A837C" }} axisLine={false} tickLine={false} />
          <YAxis tickFormatter={compact} tick={{ fontSize: 11, fill: "#8A837C" }} axisLine={false} tickLine={false} width={52} />
          <Tooltip formatter={value => euro(value)} contentStyle={{ borderRadius: 10, border: "1px solid #EAE6E0", fontSize: 12 }} />
          <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
          {mode === "bar" ? <>
            {showPrevious && <Bar dataKey="prev_invoiced" name={`Compensi ${data.previous_year}`} fill="#2563EB" fillOpacity={0.22} radius={[5, 5, 0, 0]} maxBarSize={26} />}
            {showPrevious && <Bar dataKey="prev_collected" name={`Incassato ${data.previous_year}`} fill="#0F2747" fillOpacity={0.24} radius={[5, 5, 0, 0]} maxBarSize={26} />}
            <Bar dataKey="invoiced" name="Compensi" fill="#2563EB" radius={[5, 5, 0, 0]} maxBarSize={26} />
            <Bar dataKey="collected" name="Incassato" fill="#0F2747" radius={[5, 5, 0, 0]} maxBarSize={26} />
          </> : <>
            {showPrevious && <Area type="monotone" dataKey="prev_invoiced" name={`Compensi ${data.previous_year}`} stroke="#2563EB" strokeOpacity={0.35} strokeDasharray="4 4" strokeWidth={1.6} fill="#2563EB" fillOpacity={0.07} />}
            {showPrevious && <Area type="monotone" dataKey="prev_collected" name={`Incassato ${data.previous_year}`} stroke="#0F2747" strokeOpacity={0.4} strokeDasharray="4 4" strokeWidth={1.6} fill="#0F2747" fillOpacity={0.07} />}
            <Area type="monotone" dataKey="invoiced" name="Compensi" stroke="#2563EB" strokeWidth={2} fill="url(#invoicedFill)" />
            <Area type="monotone" dataKey="collected" name="Incassato" stroke="#0F2747" strokeWidth={2} fill="url(#collectedFill)" />
          </>}
        </Chart>
      </ResponsiveContainer>
    </div>

    {!!data.top_clients.length && <footer className="finance-top" data-testid="finance-top-clients">
      <span>Incassi principali</span>
      {data.top_clients.map(row => <em key={row.name}>{row.name} <b>{euro(row.collected)}</b></em>)}
    </footer>}
  </section>;
};

const Delta = ({ value, testId }) => {
  if (value == null) return <i className="finance-delta neutral" data-testid={testId}>nuovo</i>;
  const positive = value >= 0;
  return <i className={`finance-delta ${positive ? "up" : "down"}`} data-testid={testId}>
    {positive ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
    {`${positive ? "+" : ""}${String(value).replace(".", ",")}% vs anno prec.`}
  </i>;
};
